import React, { useState } from "react";
import { Button } from "antd";
import { formatAmountDue } from "./amountDue";
import { billingApi } from "./billingApi";
import { BillingCode, describeBillingError } from "./billingCodes";
import { describeCard } from "./cards";
import { readDecline, settlePayment } from "./paymentAction";
import "./index.scss";

/**
 * The card on file tried again, for what is owed (pay-outstanding), under the
 * card form. Its bank may have declined it for a reason that has since gone —
 * funds, a limit — and nobody should have to type it in again for that. The
 * button names the amount, and pressing it is the confirmation.
 *
 * What comes of it is the billing page's, as with any fix: `onResolved` gets the
 * status it left and `{ error }`, the bank's refusal. When the server says there
 * is nothing to pay for, or no card after all, the status the page holds is out
 * of date, and `onStale` reads it again. Nothing shows without an amount owed
 * and a card to pay it with.
 */
function RetrySavedCard({ organizationId, status, onResolved, onStale }) {
   const [isPaying, setIsPaying] = useState(false);
   const [error, setError] = useState(null);

   const amount = formatAmountDue(status);
   if (!amount || !status?.hasPaymentMethod) return null;

   const card = describeCard(status.paymentMethod);

   const pay = async () => {
      if (isPaying) return;

      setIsPaying(true);
      setError(null);

      try {
         const next = await billingApi.payOutstanding({ organizationId });
         const settled = await settlePayment(next, organizationId);
         onResolved(settled.status, { error: settled.error });
      } catch (failure) {
         const decline = readDecline(failure);

         if (decline) {
            onResolved(decline.status, { error: decline.error });
         } else if (
            failure.code === BillingCode.NO_SUBSCRIPTION ||
            failure.code === BillingCode.PAYMENT_METHOD_REQUIRED
         ) {
            onStale();
         } else {
            setError(describeBillingError(failure));
         }
      } finally {
         setIsPaying(false);
      }
   };

   return (
      <div className="billing-retry-card">
         <p className="billing-note billing-note--center">
            Or try your card on file again{card ? ` (${card})` : ""}.{" "}
            <Button type="link" onClick={pay} loading={isPaying}>
               Pay {amount} with this card
            </Button>
         </p>
         {error && (
            <div className="billing-form__message" role="alert">
               {error}
            </div>
         )}
      </div>
   );
}

export default RetrySavedCard;
